import {
  PauseId,
  Object as ProtocolObject,
  Property as ProtocolProperty,
} from "@replayio/protocol";
import classNames from "classnames";
import {
  MouseEvent,
  ReactNode,
  Suspense,
  useContext,
  useRef,
  useState,
  useTransition,
} from "react";

import Expandable from "replay-next/components/Expandable";
import Icon from "replay-next/components/Icon";
import Loader from "replay-next/components/Loader";
import {
  clientValueCache,
  objectCache,
  objectPropertyCache,
} from "replay-next/src/suspense/ObjectPreviews";
import { Value as ClientValue } from "replay-next/src/utils/protocol";
import { ReplayClientContext } from "shared/client/ReplayClientContext";

import PropertiesRenderer from "./PropertiesRenderer";
import ValueRenderer from "./ValueRenderer";
import styles from "./GetterRenderer.module.css";

export default function GetterRenderer({
  parentObjectId,
  path,
  pauseId,
  protocolProperty,
}: {
  parentObjectId: string;
  path?: string;
  pauseId: PauseId;
  protocolProperty: ProtocolProperty;
}) {
  const client = useContext(ReplayClientContext);

  const [isExpanded, setIsExpanded] = useState(false);
  const [isPending, startTransition] = useTransition();
  const [invokeGetter, setInvokeGetter] = useState(false);

  const getterClientValueRef = useRef<ClientValue | null>(null);

  const { name } = clientValueCache.read(client, pauseId, protocolProperty);
  const getterValue = invokeGetter
    ? objectPropertyCache.read(client, pauseId, parentObjectId, name!)
    : null;

  if (getterClientValueRef.current === null && getterValue != null) {
    getterClientValueRef.current = clientValueCache.read(client, pauseId, getterValue);
  }

  const getterClientValue = getterClientValueRef.current;
  let getterValueWithPreview: ProtocolObject | null = null;
  let showExpandableView = false;
  switch (getterClientValue?.type) {
    case "array":
    case "function":
    case "html-element":
    case "html-text":
    case "map":
    case "object":
    case "regexp":
    case "set": {
      getterValueWithPreview = objectCache.read(
        client,
        pauseId,
        getterClientValue.objectId!,
        "canOverflow"
      );
      if (getterValueWithPreview == null) {
        throw Error(`Could not find object with ID "${getterClientValue.objectId}"`);
      }

      showExpandableView = true;
    }
  }

  let value: ReactNode = null;
  if (getterValue) {
    if (!isExpanded) {
      value = (
        <span data-test-name="GetterRenderer-Value">
          <ValueRenderer
            context="nested"
            layout="vertical"
            pauseId={pauseId}
            protocolValue={getterValue}
          />
        </span>
      );
    }
  } else {
    value = (
      <button
        className={styles.InvokeGetterButton}
        data-test-name="GetterRenderer-LoadValueButton"
        disabled={isPending}
        onClick={(event: MouseEvent) => {
          event.stopPropagation();

          startTransition(() => {
            setInvokeGetter(true);
          });
        }}
      >
        <Icon className={styles.InvokeGetterIcon} type="invoke-getter" />
      </button>
    );
  }

  const header = (
    <span
      className={classNames(
        styles.GetterRenderer,
        !showExpandableView ? styles.ToggleAlignmentPadding : null
      )}
      data-test-name="GetterRenderer"
    >
      <span className={styles.Name} data-test-name="GetterRenderer-Name">
        {name}
      </span>
      <span className={styles.Separator}>: </span>
      {value}
    </span>
  );

  if (showExpandableView) {
    return (
      <Expandable
        children={
          <Suspense fallback={<Loader />}>
            <PropertiesRenderer object={getterValueWithPreview!} path={path} pauseId={pauseId} />
          </Suspense>
        }
        header={header}
        onChange={setIsExpanded}
        persistenceKey={path}
      />
    );
  } else {
    return header;
  }
}
